import {
  CategoryEnum,
  OfferData,
  OfferItem,
  OfferCardTypeEnum,
} from '@wwe-portal/entities/offer.type';
import { DEFAULT_DRIP_DAYS } from '@wwe-portal/ui/features/offer/type';
import {
  MAXIMUM_END_TIME_DAYS,
  QUANTITY_SHORTED_THRESHOLD,
} from '../constants/offer';
import { daysToMs, getDateTimeWithZone } from '@wwe-portal/utils/date';
import { getCardStatus, getStockQuantity } from './mapper';

export const getNameCurrencies = (item: Partial<OfferItem>) => {
  let item_id = item?.itemID;
  let item_name = item?.itemName;
  const isRuby =
    Boolean(item?.itemName?.includes('Rubies')) ||
    Boolean(item?.itemID?.includes('Ruby'));
  const isWebcoin =
    Boolean(item?.itemName?.includes('Web Coins')) ||
    Boolean(item?.itemName?.includes('WebCoin'));
  const isPick =
    Boolean(item?.itemName?.includes('Pick')) ||
    Boolean(item?.itemID?.includes('Pick'));

  if (isRuby) {
    item_id = 'web_currency_ruby';
    item_name = 'ruby';
  }
  if (isWebcoin) {
    item_id = 'web_currency_web_coin';
    item_name = 'web_coin';
  }
  if (isPick) {
    item_id = 'web_currency_pick';
    item_name = 'pick';
  }
  return [item_id, item_name];
};

export const getCurrencyFromStorage = (
  typeCurrency: string | null | undefined
) => {
  if (
    (typeCurrency && typeCurrency === 'webCoins') ||
    typeCurrency === 'web-coin'
  ) {
    return 'web_coins';
  }
  if (typeCurrency === 'cashPacks') {
    return 'cash_packs';
  }
  return typeCurrency;
};

export function isDripOffer(offer: Partial<OfferData>) {
  return Boolean(offer.dc?.b1 && Array.isArray(offer.dc.b1));
}

export function isOneTimeOffer(offer: Partial<OfferData> | undefined) {
  return offer?.OfferType === 'OneTimeOffer';
}

export function hasDripConflict(
  offer: Partial<OfferData>,
  activeDripDurations: Set<number>
): boolean {
  if (!isDripOffer(offer)) return false;
  const duration = offer.dc?.b0 ?? DEFAULT_DRIP_DAYS;
  return activeDripDurations.has(duration);
}

export const isRewardOffer = (offer: Partial<OfferData> | undefined) =>
  Boolean(
    offer?.AlternativeCostAmount === 0 &&
      offer.OfferType === 'AlternativeCurrency'
  );

export const isAlternativeOffer = (offer: Partial<OfferData> | undefined) =>
  Boolean(
    offer?.AlternativeCostAmount && offer.OfferType === 'AlternativeCurrency'
  );

export const getIsFreeClaimTournamentOffer = (
  offerType: string,
  data: Partial<OfferData>
) =>
  offerType === 'tournament' &&
  !data?.AlternativeCostAmount &&
  data?.OfferType === 'AlternativeCurrency';

export const isWebcoinOffer = (offerData: Partial<OfferData>) => {
  return Boolean(offerData?.StoreItemName?.includes('Web Coins'));
};

export const isRubyOffer = (offerData: Partial<OfferData>) => {
  return Boolean(offerData?.StoreItemName?.includes('Rubies'));
};

export const isCashPack = (offerData: Partial<OfferData>) => {
  return Boolean(offerData?.StoreItemName?.includes('Pack'));
};

export const isWebCoinItem = (itemName: string) => {
  return Boolean(itemName.includes('Web Coins'));
};

export const isRubyItem = (itemName: string) => {
  return Boolean(itemName.includes('Rubies'));
};

type QuantityValue = string | number | undefined;

export const formatQuantity = (quantity: QuantityValue) => {
  const quanNumber = Number(quantity);
  if (
    isNaN(quanNumber) ||
    quanNumber < QUANTITY_SHORTED_THRESHOLD ||
    quanNumber % QUANTITY_SHORTED_THRESHOLD !== 0
  )
    return String(quantity);
  return Intl.NumberFormat('en', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(quanNumber);
};

function formatNumberCompactFloor(num: number) {
  const units = [
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' },
  ];

  for (let i = 0; i < units.length; i++) {
    const unit = units[i];
    if (unit && num >= unit.value) {
      const floored = Math.floor((num / unit.value) * 100) / 100;
      return `${floored}${unit.symbol}`;
    }
  }
  return num.toString();
}

export const formatQuantityByDouble = (quantity: QuantityValue) => {
  const quanNumber = Number(quantity);
  if (isNaN(quanNumber) || quanNumber < QUANTITY_SHORTED_THRESHOLD)
    return String(quantity);

  return formatNumberCompactFloor(quanNumber);
};

export const getDescriptionItem = (
  quantity: QuantityValue,
  name: string | undefined
) => {
  if (!quantity) return name;
  return `${formatQuantity(quantity)} ${name}`;
};

export const getEndDate = (endDate: string | undefined | number) => {
  if (!endDate) return undefined;

  const utcEndDate = getDateTimeWithZone(new Date(endDate), 'UTC', {
    keepLocalTime: true,
  });

  const endTimestamp = utcEndDate.getTime();

  if (endTimestamp - new Date().getTime() > daysToMs(MAXIMUM_END_TIME_DAYS))
    return undefined;

  return utcEndDate;
};

export const getOfferDate = (date?: string) => {
  if (!date) return null;
  return getDateTimeWithZone(new Date(date), 'UTC', {
    keepLocalTime: true,
  });
};

export const isOfferStarted = (offer: { StartDate?: string }) => {
  try {
    const offerStartDateUTC = getOfferDate(offer.StartDate);
    if (!offerStartDateUTC) return true;
    return offerStartDateUTC.getTime() <= Date.now();
  } catch {
    return false;
  }
};

export const isOfferEnded = (offer: { EndDate?: string }) => {
  try {
    const offerEndDateUTC = getOfferDate(offer.EndDate);
    if (!offerEndDateUTC) return false;
    return offerEndDateUTC.getTime() <= Date.now();
  } catch {
    return false;
  }
};

export const isOfferValidInTimeRange = (offer: OfferData) => {
  // Skip time range check for daily offers
  if (offer.categoryId === CategoryEnum.WEBCOIN_DAILY || offer?.isDailyOffer) {
    return true;
  }
  return isOfferStarted(offer) && !isOfferEnded(offer);
};

export const isSupportedOfferCombinationType = (offer: OfferData) =>
  !!offer?.CombinationType &&
  Object.values(OfferCardTypeEnum).includes(
    offer.CombinationType as OfferCardTypeEnum
  );

export const filterOfferByTimeRange = (offers?: OfferData[] | undefined) => {
  if (!offers) return [];
  return offers.filter(isOfferValidInTimeRange) ?? [];
};

export const filterDailyOffers = (offers?: OfferData[] | undefined) => {
  return filterOfferByTimeRange(offers).filter(
    (offer) => offer.PortalAvailability
  );
};

export const getOfferMap = (offers?: OfferData[] | undefined) => {
  return new Map<string, OfferData>(
    offers?.map((offer) => [offer.OffersID, offer]) ?? []
  );
};

export const isOfferPurchasable = (
  offerData: OfferData,
  {
    isAuthenticated,
    stockMap,
    isSegmentedOffer,
  }: {
    isAuthenticated: boolean;
    stockMap: Map<string, number> | undefined;
    isSegmentedOffer: ((offer: OfferData) => boolean) | undefined;
  }
) => {
  if (!isAuthenticated && isOneTimeOffer(offerData)) return false;
  if (isSegmentedOffer && !isSegmentedOffer(offerData)) return false;
  if (!isOfferValidInTimeRange(offerData)) return false;
  if (!isSupportedOfferCombinationType(offerData)) return false;

  const stockQuantity = getStockQuantity(offerData, {
    isAuthenticated,
    stockMap,
  });
  if (!stockQuantity || stockQuantity <= 0) return false;
  const { isComingSoon, isNotReady } = getCardStatus(offerData, stockMap);
  return !isComingSoon && !isNotReady;
};
